/**
 * Enhanced Forecast Copy Helper for Duplicate FTD Component (with Immediate Recalculation)
 *
 * This version provides immediate recalculation after copying by manually calling
 * the calculateValues function for all affected months.
 */

import {
  ComponentConfig,
  CopyType,
  MonthlyDataEntry,
  getTargetMonths,
  getCurrentMetricKeyGeneric,
} from "../../CreateNewForeCast/helpers/Forecastcopyhelper";

/**
 * Copy a metric value from source month to target months in duplicate data structure
 */
export const copyMetricToMonthsDuplicate = (
  monthlyDataDuplicate: Record<string, MonthlyDataEntry>,
  sourceMonth: string,
  metricKey: string,
  copyType: CopyType,
): Record<string, MonthlyDataEntry> => {
  const sourceData = monthlyDataDuplicate[sourceMonth];
  if (!sourceData || sourceData[metricKey] === undefined) {
    console.warn(`No data found for ${sourceMonth}.${metricKey}`);
    return monthlyDataDuplicate;
  }

  const valueToUpdate = sourceData[metricKey];
  const targetMonths = getTargetMonths(sourceMonth, copyType);

  const updatedData = { ...monthlyDataDuplicate };

  targetMonths.forEach((targetMonth: any) => {
    if (updatedData[targetMonth]) {
      updatedData[targetMonth] = {
        ...updatedData[targetMonth],
        [metricKey]: valueToUpdate,
      };
    }
  });

  return updatedData;
};

/**
 * Generic copy handler with immediate recalculation
 *
 * This version manually recalculates all affected months immediately after copying,
 * providing instant feedback without waiting for useRecalculateMonthlyData to trigger.
 *
 * @param copyType - Type of copy operation (month, quarter, year)
 * @param editableStates - Object containing all editable state values
 * @param monthlyDataDuplicate - Current duplicate monthly data
 * @param config - Component configuration
 * @param monthNames - Array of month names
 * @param calculateValues - Function to calculate derived values for a month
 * @param lastMonthActiveUsersDuplicate - Active users from last month of previous year
 * @param setMonthlyDataDuplicateAction - Function to update monthly data
 */
export const handleCopyToNextDuplicateWithRecalc = (
  copyType: CopyType,
  editableStates: Record<string, string | null>,
  monthlyDataDuplicate: Record<string, MonthlyDataEntry>,
  config: ComponentConfig,
  monthNames: string[],
  calculateValues: (
    month: string,
    newValues: any,
    forcePreviousMonthActiveUsers?: number,
  ) => any,
  lastMonthActiveUsersDuplicate: number,
  setMonthlyDataDuplicateAction: (
    data: Record<string, MonthlyDataEntry>,
  ) => void,
) => {
  // Get current metric info
  const currentMetric = getCurrentMetricKeyGeneric(editableStates, config);

  if (!currentMetric) {
    console.error(`No metric is currently being edited in ${config.name}`);
    return;
  }

  const { month: sourceMonth, metricKey } = currentMetric;

  // Copy the metric to target months
  let updatedMonthlyData = copyMetricToMonthsDuplicate(
    monthlyDataDuplicate,
    sourceMonth,
    metricKey,
    copyType,
  );

  // Find the source month index to start recalculation
  const sourceMonthIndex = monthNames.indexOf(sourceMonth);

  // Recalculate all months from source month onwards
  for (let i = sourceMonthIndex; i < monthNames.length; i++) {
    const month = monthNames[i];
    if (updatedMonthlyData[month]) {
      let previousMonthActiveUsers;

      if (i === 0) {
        // January uses lastMonthActiveUsersDuplicate
        previousMonthActiveUsers = lastMonthActiveUsersDuplicate;
      } else {
        // Use previous month's calculated activeUsers
        const prevMonth = monthNames[i - 1];
        previousMonthActiveUsers = updatedMonthlyData[prevMonth].activeUsers;
      }

      // Recalculate with the updated values
      updatedMonthlyData[month] = calculateValues(
        month,
        updatedMonthlyData[month],
        previousMonthActiveUsers,
      );
    }
  }

  // Update the state with recalculated data
  setMonthlyDataDuplicateAction(updatedMonthlyData);
};

/**
 * Simple copy handler without immediate recalculation
 *
 * Use this if you want to rely on useRecalculateMonthlyData hook for recalculation
 */
export const handleCopyToNextDuplicate = (
  copyType: CopyType,
  editableStates: Record<string, string | null>,
  monthlyDataDuplicate: Record<string, MonthlyDataEntry>,
  config: ComponentConfig,
  setMonthlyDataDuplicateAction: (
    data: Record<string, MonthlyDataEntry>,
  ) => void,
) => {
  const currentMetric = getCurrentMetricKeyGeneric(editableStates, config);

  if (!currentMetric) {
    console.error(`No metric is currently being edited in ${config.name}`);
    return;
  }

  const { month: sourceMonth, metricKey } = currentMetric;

  const updatedMonthlyData = copyMetricToMonthsDuplicate(
    monthlyDataDuplicate,
    sourceMonth,
    metricKey,
    copyType,
  );

  setMonthlyDataDuplicateAction(updatedMonthlyData);
};
